import React, { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
} from "reactstrap"
import QwikForm from "components/form/QwikForm"
import QwikInput from "components/form/QwikInput"
import QwikSelect from "components/form/QwikSelect"
import { formatISO } from "date-fns"
import { yupResolver } from "@hookform/resolvers/yup"
import { clientLeadsSchema } from "schemas/clientLeadsSchema"
import { useCreateClientLeadMutation } from "store/features/clientLeads/clientLeadsManagement"
import { toast } from "sonner"

const AddLeadModal = ({ isOpen, toggle, onLeadCreated }) => {
  const [createClientLead, { isLoading }] = useCreateClientLeadMutation()
  const [projectProposal, setProjectProposal] = useState(null)
  const [contract, setContract] = useState(null)

  const handleFileChange = (setter, event) => {
    const file = event.target.files[0]
    setter(file)
  }

  const formatDateSafe = dateVal => {
    if (!dateVal) return null
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal)
    return isNaN(d.getTime()) ? null : formatISO(d)
  }

  const handleSubmit = async (data, methods) => {
    try {
      const startDate = formatDateSafe(data.start_date)
      const endDate = formatDateSafe(data.end_date)
      const followUpDate = formatDateSafe(data.follow_up_date)

      const followUpTime =
        data.follow_up_time && data.follow_up_time !== ""
          ? formatISO(new Date(`1970-01-01T${data.follow_up_time}`))
          : null

      const summaryArray = data.summary
        ? [
            {
              id: 1,
              note: data.summary,
            },
          ]
        : []

      // Create FormData for file upload
      const formData = new FormData()

      // Append all text fields
      formData.append("name", data.name || "")
      formData.append("email", data.email || "")
      formData.append("company", data.company || "")
      formData.append("phone", data.phone || "")
      formData.append("service_name", data.service_name || "")
      formData.append("requirements", data.requirements || "")
      formData.append("status", data.status || "")
      formData.append("priority_status", data.priority_status || "")
      if (startDate) formData.append("start_date", startDate)
      if (endDate) formData.append("end_date", endDate)
      if (followUpDate) formData.append("follow_up_date", followUpDate)
      if (followUpTime) formData.append("follow_up_time", followUpTime)
      if (summaryArray.length > 0) {
        formData.append("summary", JSON.stringify(summaryArray))
      }

      // Append files if they exist
      if (projectProposal) {
        formData.append("project_proposal", projectProposal)
      }
      if (contract) {
        formData.append("contract", contract)
      }

      const response = await createClientLead(formData)

      if (response?.data?.id) {
        setProjectProposal(null)
        setContract(null)

        const projectProposalInput = document.getElementById(
          "modal_project_proposal"
        )
        const contractInput = document.getElementById("modal_contract")
        if (projectProposalInput) projectProposalInput.value = ""
        if (contractInput) contractInput.value = ""

        methods.reset({}, { keepValues: false, keepErrors: true })
        if (onLeadCreated) {
          onLeadCreated()
        }
        toggle()
      } else {
        toast.error("Error creating client lead")
      }
    } catch (error) {
      console.error("Submission error:", error)
      toast.error("Failed to create client lead")
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Add New Lead</ModalHeader>
      <QwikForm
        onSubmit={handleSubmit}
        resolver={yupResolver(clientLeadsSchema)}
        encType="multipart/form-data"
      >
        <ModalBody>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="name"
                  label="Name"
                  placeholder="Enter Name"
                  type="text"
                  className="form-control"
                  required={true}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="phone"
                  label="Phone"
                  placeholder="Enter Phone"
                  type="text"
                  className="form-control"
                  required={true}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="email"
                  label="Email"
                  placeholder="Enter Email"
                  type="email"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="company"
                  label="Company Name"
                  placeholder="Enter Company Name"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="service_name"
                  label="Service Name"
                  options={[
                    {
                      value: "Website Development",
                      label: "Website Development",
                    },
                    {
                      value: "Mobile App - Android",
                      label: "Mobile App - Android",
                    },
                    {
                      value: "Mobile App - iOS",
                      label: "Mobile App - iOS",
                    },
                    {
                      value: "Mobile App - Android & iOS (Both)",
                      label: "Mobile App - Android & iOS (Both)",
                    },
                    {
                      value: "Website + Mobile App",
                      label: "Website + Mobile App",
                    },
                    {
                      value: "E-Commerce Solution",
                      label: "E-Commerce Solution",
                    },
                    {
                      value: "POS System",
                      label: "POS System",
                    },
                    {
                      value: "CRM / ERP System",
                      label: "CRM / ERP System",
                    },
                    {
                      value: "Service Platform (App / Web)",
                      label: "Service Platform (App / Web)",
                    },
                    {
                      value: "Digital Marketing",
                      label: "Digital Marketing",
                    },
                    {
                      value: "Custom Software Development",
                      label: "Custom Software Development",
                    },
                    {
                      value: "Website + Mobile App (Android & iOS) + CRM",
                      label: "Website + Mobile App (Android & iOS) + CRM",
                    },
                    {
                      value: "Website + Mobile App (Android & iOS) + CRM + ERP",
                      label: "Website + Mobile App (Android & iOS) + CRM + ERP",
                    },
                    {
                      value:
                        "All-in-One Solution (Website, App, CRM, POS & Marketing)",
                      label:
                        "All-in-One Solution (Website, App, CRM, POS & Marketing)",
                    },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="priority_status"
                  label="Priority Status"
                  options={[
                    { value: "1", label: "High" },
                    { value: "2", label: "Medium" },
                    { value: "3", label: "Low" },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="status"
                  label="Status"
                  options={[
                    { value: "Not received", label: "Not received" },
                    { value: "Busy", label: "Busy" },
                    { value: "Sent demo", label: "Sent demo" },
                    { value: "Sent proposal", label: "Sent proposal" },
                    {
                      value: "Wanna visit office",
                      label: "Wanna visit office",
                    },
                    {
                      value: "Wanna zoom meeting",
                      label: "Wanna zoom meeting",
                    },
                    {
                      value: "Sent proposal but no response",
                      label: "Sent proposal but no response",
                    },
                    {
                      value: "Sent demo, he will review and back to us",
                      label: "Sent demo, he will review and back to us",
                    },
                    {
                      value: "Knocked by mistake",
                      label: "Knocked by mistake",
                    },
                    {
                      value: "He has low budget",
                      label: "He has low budget",
                    },
                    {
                      value: "He will knock us when he need",
                      label: "He will knock us when he need",
                    },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="requirements"
                  label="Requirements"
                  placeholder="Enter Requirements"
                  type="textarea"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <QwikInput
                  name="start_date"
                  label="Start Date"
                  placeholder="Enter Start Date"
                  type="date"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <QwikInput
                  name="end_date"
                  label="End Date"
                  placeholder="Enter End Date"
                  type="date"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <QwikInput
                  name="follow_up_date"
                  label="Follow Up Date"
                  placeholder="Enter Follow Up Date"
                  type="date"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <QwikInput
                  name="follow_up_time"
                  label="Follow Up Time"
                  placeholder="Enter Follow Up Time"
                  type="time"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={4}>
              <FormGroup>
                <Label for="modal_project_proposal" className="form-label">
                  Project Proposal
                </Label>
                <div className="custom-file">
                  <input
                    type="file"
                    className="custom-file-input form-control"
                    id="modal_project_proposal"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={e => handleFileChange(setProjectProposal, e)}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col lg={4}>
              <FormGroup>
                <Label for="modal_contract" className="form-label">
                  Contract
                </Label>
                <div className="custom-file">
                  <input
                    type="file"
                    className="custom-file-input form-control"
                    id="modal_contract"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={e => handleFileChange(setContract, e)}
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={toggle}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading ? "Creating..." : "Add Lead"}
          </Button>
        </ModalFooter>
      </QwikForm>
    </Modal>
  )
}

export default AddLeadModal
